import React from "react";
import {
  money,
  formatDate,
  clienteName,
  proyectoTotal,
  estadoClass,
  daysUntil,
  statusMedico,
  statusPRL,
} from "@/lib/helpers";

export default function Dashboard({ state, setView }) {
  const kpis = [
    {
      key: "clientes", label: "Clientes", value: state.clientes.length, hint: "Activos",
      target: "clientes", bg: "bg-blue-100",
      icon: (<svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87"/></svg>),
    },
    {
      key: "articulos", label: "Artículos", value: state.articulos.length, hint: "En catálogo",
      target: "articulos", bg: "bg-orange-100",
      icon: (<svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M21 16V8l-9-4-9 4v8l9 4 9-4z"/></svg>),
    },
    {
      key: "servicios", label: "Servicios", value: state.servicios.length, hint: "Disponibles",
      target: "servicios", bg: "bg-purple-100",
      icon: (<svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M12 2l2.39 5.48L20 9l-4.32 3.76L17 19l-5-2.9L7 19l1.32-6.24L4 9l5.61-1.52L12 2z"/></svg>),
    },
    {
      key: "personal", label: "Personal", value: state.personal.length, hint: "Plantilla",
      target: "personal", bg: "bg-emerald-100",
      icon: (<svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M3 7h18v10H3z"/></svg>),
    },
    {
      key: "proyectos", label: "Proyectos", value: state.proyectos.length, hint: "Activos",
      target: "proyectos", bg: "bg-indigo-100",
      icon: (<svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M3 7a2 2 0 012-2h3l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>),
    },
    {
      key: "ventas", label: "Facturación",
      value: money(state.ventas.reduce((s, v) => s + (+v.total || 0), 0)),
      hint: `${state.ventas.length} ventas`, target: "ventas", bg: "bg-amber-100",
      icon: (<svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M3 3h2l2 12h13l2-9H6"/></svg>),
    },
  ];

  const lastProyectos = [...state.proyectos].slice(-5).reverse();
  const lastVentas = [...state.ventas].slice(-5).reverse();

  const dashboardProyectos = [...state.proyectos]
    .sort((a, b) => {
      const ea = a.estado === "Finalizado" || a.estado === "Facturado" ? 1 : 0;
      const eb = b.estado === "Finalizado" || b.estado === "Facturado" ? 1 : 0;
      if (ea !== eb) return ea - eb;
      return (+b.progreso || 0) - (+a.progreso || 0);
    })
    .slice(0, 6);

  const avgProgreso = (() => {
    const act = state.proyectos.filter((p) => p.estado !== "Finalizado" && p.estado !== "Facturado");
    if (act.length === 0) return 0;
    return Math.round(act.reduce((s, p) => s + (+p.progreso || 0), 0) / act.length);
  })();

  const enCursoCount = state.proyectos.filter(
    (p) => p.estado === "En curso" || ((+p.progreso || 0) > 0 && (+p.progreso || 0) < 100)
  ).length;

  const progresoColor = (v) => {
    v = +v || 0;
    if (v >= 100) return "text-emerald-600";
    if (v >= 66) return "text-[var(--cvt-blue)]";
    if (v >= 33) return "text-[var(--cvt-orange)]";
    return "text-slate-600";
  };
  const deadlineText = (p) => {
    if (!p.fechaFin) return "—";
    const d = daysUntil(p.fechaFin);
    if (d < 0) return `${Math.abs(d)}d de retraso`;
    if (d === 0) return "Vence hoy";
    return `${d}d restantes`;
  };
  const deadlineColor = (p) => {
    if (!p.fechaFin) return "text-slate-400";
    const d = daysUntil(p.fechaFin);
    if (d < 0) return "text-red-600";
    if (d <= 7) return "text-[var(--cvt-orange)]";
    return "text-slate-500";
  };

  const alertsMedico = state.personal
    .filter((p) => {
      const d = daysUntil(p.fechaReconocimientoMedico);
      return d !== null && d <= 30;
    })
    .map((p) => {
      const d = daysUntil(p.fechaReconocimientoMedico);
      return {
        id: p.id + "_m", nombre: p.nombre, critical: d < 0,
        msg: d < 0 ? `Reconocimiento médico caducado hace ${Math.abs(d)} días`
          : `Reconocimiento médico vence en ${d} días`,
      };
    });
  const alertsPRL = state.personal
    .filter((p) => {
      const d = daysUntil(p.fechaCaducidadPRL);
      return d !== null && d <= 30;
    })
    .map((p) => {
      const d = daysUntil(p.fechaCaducidadPRL);
      return {
        id: p.id + "_prl", nombre: p.nombre, critical: d < 0,
        msg: d < 0 ? `PRL caducado hace ${Math.abs(d)} días` : `PRL vence en ${d} días`,
      };
    });

  return (
    <section className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k) => (
          <div key={k.key} className="card kpi-card p-5" onClick={() => setView(k.target)}
               data-testid={`kpi-${k.key}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  {k.label}
                </div>
                <div className="text-2xl font-extrabold mt-1">{k.value}</div>
              </div>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.bg}`}>
                {k.icon}
              </div>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* EVOLUCIÓN */}
      <div className="card p-6 relative overflow-hidden"
           style={{ background: "linear-gradient(135deg,#fff 0%,#fff 55%,rgba(247,148,29,.06) 100%)" }}>
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
             style={{ background: "radial-gradient(circle,rgba(41,171,226,.12) 0%,transparent 70%)" }}/>
        <div className="flex items-center justify-between mb-5 relative">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-6 rounded bg-gradient-to-b from-[var(--cvt-orange)] to-[var(--cvt-blue)]"/>
              <h2 className="font-extrabold text-lg tracking-tight">Evolución de obras</h2>
            </div>
            <p className="text-xs text-slate-500 ml-4">Progreso en tiempo real · ordena y prioriza tus obras</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="text-right">
              <div className="text-slate-500">Progreso medio</div>
              <div className="text-xl font-extrabold text-[var(--cvt-orange)]">{avgProgreso}%</div>
            </div>
            <div className="h-10 w-px bg-slate-200"/>
            <div className="text-right">
              <div className="text-slate-500">En curso</div>
              <div className="text-xl font-extrabold text-[var(--cvt-blue)]">{enCursoCount}</div>
            </div>
            <button className="btn btn-ghost !py-1.5" onClick={() => setView("proyectos")}
                    data-testid="dashboard-see-proyectos">Ver todas →</button>
          </div>
        </div>
        {state.proyectos.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-10">
            Aún no hay proyectos · crea tu primera obra desde el módulo Proyectos
          </div>
        )}
        {state.proyectos.length > 0 && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 relative">
            {dashboardProyectos.map((p) => (
              <div key={p.id}
                   className="group p-4 rounded-xl border border-slate-200 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
                   onClick={() => setView("proyectos")}
                   data-testid={`evo-proyecto-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{p.nombre}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {clienteName(state, p.clienteId)}
                    </div>
                  </div>
                  <span className={`badge ml-2 shrink-0 ${estadoClass(p.estado)}`}>{p.estado}</span>
                </div>
                <div className="flex items-end justify-between mb-1">
                  <span className={`text-3xl font-black tracking-tight ${progresoColor(p.progreso)}`}>
                    {(+p.progreso || 0) + "%"}
                  </span>
                  <span className={`text-[11px] font-semibold uppercase tracking-wide ${deadlineColor(p)}`}>
                    {deadlineText(p)}
                  </span>
                </div>
                <div className="progress-track mb-3">
                  <div className="progress-bar"
                       style={{ width: `${Math.max(0, Math.min(100, +p.progreso || 0))}%` }}/>
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>Inicio · <span className="font-semibold text-slate-700">{formatDate(p.fechaInicio)}</span></span>
                  <span>Fin · <span className="font-semibold text-slate-700">{formatDate(p.fechaFin)}</span></span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                  <div className="flex gap-2 text-[11px]">
                    <span className="chip">{(p.articulos || []).length} art</span>
                    <span className="chip">{(p.servicios || []).length} serv</span>
                    <span className="chip">{(p.personalIds || []).length} pers</span>
                  </div>
                  <span className="font-bold text-[var(--cvt-orange)] text-sm">
                    {money(proyectoTotal(p))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-base">Avisos · Reconocimientos médicos</h2>
            <span className="chip">{alertsMedico.length} avisos</span>
          </div>
          {alertsMedico.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">Sin avisos · todos los reconocimientos al día.</div>
          ) : (
            alertsMedico.map((a) => (
              <div key={a.id} className={`alert-row ${a.critical ? "critical" : ""}`}>
                <div>
                  <div className="font-semibold text-sm">{a.nombre}</div>
                  <div className="text-xs text-slate-600">{a.msg}</div>
                </div>
                <button className="btn btn-ghost !py-1 !text-xs" onClick={() => setView("personal")}>Ver</button>
              </div>
            ))
          )}
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-base">Avisos · Caducidad PRL</h2>
            <span className="chip">{alertsPRL.length} avisos</span>
          </div>
          {alertsPRL.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">Sin avisos · todo el PRL vigente.</div>
          ) : (
            alertsPRL.map((a) => (
              <div key={a.id} className={`alert-row ${a.critical ? "critical" : ""}`}>
                <div>
                  <div className="font-semibold text-sm">{a.nombre}</div>
                  <div className="text-xs text-slate-600">{a.msg}</div>
                </div>
                <button className="btn btn-ghost !py-1 !text-xs" onClick={() => setView("personal")}>Ver</button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-base">Últimos proyectos</h2>
            <button className="text-xs font-semibold text-[var(--cvt-blue)]" onClick={() => setView("proyectos")}>Ver todos →</button>
          </div>
          <table className="data">
            <thead><tr><th>Proyecto</th><th>Cliente</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {lastProyectos.map((p) => (
                <tr key={p.id}>
                  <td>{p.nombre}</td>
                  <td>{clienteName(state, p.clienteId)}</td>
                  <td className="text-right font-semibold">{money(proyectoTotal(p))}</td>
                </tr>
              ))}
              {lastProyectos.length === 0 && (
                <tr><td colSpan="3" className="text-center text-slate-400 py-4">Sin proyectos aún</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-base">Últimas ventas</h2>
            <button className="text-xs font-semibold text-[var(--cvt-blue)]" onClick={() => setView("ventas")}>Ver todas →</button>
          </div>
          <table className="data">
            <thead><tr><th>Nº</th><th>Cliente</th><th>Fecha</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {lastVentas.map((v) => (
                <tr key={v.id}>
                  <td className="font-semibold">{v.numero}</td>
                  <td>{clienteName(state, v.clienteId)}</td>
                  <td>{formatDate(v.fecha)}</td>
                  <td className="text-right font-semibold">{money(v.total)}</td>
                </tr>
              ))}
              {lastVentas.length === 0 && (
                <tr><td colSpan="4" className="text-center text-slate-400 py-4">Sin ventas aún</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
